import type { Injector } from '@angular/core';
import { inject, runInInjectionContext } from '@angular/core';
import type { MatDialogRef } from '@angular/material/dialog';
import { assertInjector } from '@quipex/core/utils';
import {
    DialogService,
    isDialogConfirmedOnly,
    type DialogResult,
} from '@quipex/shared/ui';
import { take } from 'rxjs';
import { TransferBuildingOwnershipDialog } from './transfer-building-ownership-dialog';

export interface TransferBuildingOwnershipDialogRequest {
    buildingId: string;
    buildingName: string;
    onStarted?: () => void;
}

export interface TransferBuildingOwnershipDialogLauncher {
    open: (
        request: TransferBuildingOwnershipDialogRequest
    ) => MatDialogRef<TransferBuildingOwnershipDialog, boolean | DialogResult>;
}

export function injectTransferBuildingOwnershipDialog(
    injector?: Injector
): TransferBuildingOwnershipDialogLauncher {
    const resolvedInjector = assertInjector(
        injectTransferBuildingOwnershipDialog,
        injector
    );

    const open = (
        request: TransferBuildingOwnershipDialogRequest
    ): MatDialogRef<
        TransferBuildingOwnershipDialog,
        boolean | DialogResult
    > => {
        return runInInjectionContext(resolvedInjector, () => {
            const dialogService = inject(DialogService);

            const dialogRef = dialogService.openDialog(
                {
                    component: TransferBuildingOwnershipDialog,
                    componentInputs: {
                        buildingId: request.buildingId,
                        buildingName: request.buildingName,
                    },
                },
                {
                    size: 'md',
                    panelClass: ['flex-content', 'sm-h-min-container'],
                    ariaLabel: 'Transfer building ownership',
                }
            );

            dialogRef
                .afterClosed()
                .pipe(isDialogConfirmedOnly(), take(1))
                .subscribe(() => {
                    request.onStarted?.();
                });

            return dialogRef;
        });
    };

    return { open };
}
